/**
 * User Management - Enterprise Dashboard
 * Gestione completa utenti con ruoli granulari SOLO CON ID DISCORD
 */

import { useState } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
// import { motion, AnimatePresence } from 'framer-motion'
import { AdminLayout } from "../../layout/AdminLayout";
import { EnterprisePageHeader } from "../../components/layout/EnterprisePageHeader";
import { EnterpriseEmptyState } from "../../components/layout/EnterpriseEmptyState";
import { EnterpriseSectionSkeleton } from "../../components/layout/EnterpriseSectionSkeleton";
import { EnterpriseStickyActionBar } from "../../components/layout/EnterpriseStickyActionBar";
import { useUserManagement, UserRole } from "@/hooks/useUserManagement";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Input } from "@/shared/components/ui/input";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/shared/components/ui/tabs";
import { toast } from "@/shared/hooks/use-toast";
import {
    Users,
    Activity,
    Search,
    UserCheck,
    Crown,
    ShieldCheck,
    UserCog,
    Clock,
    AlertCircle,
    CheckCircle,
    Settings,
    Ban,
    Unlock,
    Smartphone,
    Globe,
    TrendingUp,
} from "lucide-react";

export default function UsersManagement() {
    const { user, logout } = useAuthContext();
    const {
        users,
        selectedUser,
        userSessions,
        auditLogs,
        isLoading,
        error,
        selectUser,
        applyFilters,
        updateUserRole,
        toggleUserStatus,
        refresh,
    } = useUserManagement();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRole, setSelectedRole] = useState<UserRole | "">("");
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const handleLogout = () => {
        void logout();
    };

    const handleSearch = () => {
        applyFilters({
            search: searchTerm || undefined,
            role: selectedRole || undefined,
            active_only: showActiveOnly || undefined,
        });
        toast({
            title: "Filtri applicati",
            description: "La lista utenti e stata aggiornata.",
        });
    };

    const handleRoleChange = async (userId: number, newRole: UserRole) => {
        setActionLoading(`role-${userId}`);
        try {
            const result = await updateUserRole(userId, newRole);
            if (!result.success) {
                toast({
                    title: "Cambio ruolo fallito",
                    description: result.error || "Errore sconosciuto",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Ruolo aggiornato",
                    description: `Nuovo ruolo: ${newRole.replace("_", " ")}`,
                });
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleStatus = async (userId: number) => {
        setActionLoading(`status-${userId}`);
        try {
            const result = await toggleUserStatus(userId);
            if (!result.success) {
                toast({
                    title: "Aggiornamento stato fallito",
                    description: result.error || "Errore sconosciuto",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Stato utente aggiornato",
                    description: "L'account e stato aggiornato correttamente.",
                });
            }
        } finally {
            setActionLoading(null);
        }
    };
    const getRoleBadge = (role: UserRole) => {
        switch (role) {
            case "SUPER_ADMIN":
                return (
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                        <Crown className="h-3 w-3 mr-1" />
                        Super Admin
                    </Badge>
                );
            case "ADMIN":
                return (
                    <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                        <ShieldCheck className="h-3 w-3 mr-1" />
                        Admin
                    </Badge>
                );
            case "MARRANI":
                return (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Marrani
                    </Badge>
                );
            case "TRIAL":
                return (
                    <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                        <Clock className="h-3 w-3 mr-1" />
                        Trial
                    </Badge>
                );
            default:
                return <Badge variant="outline">Unknown</Badge>;
        }
    };

    const getStatusBadge = (isActive: boolean) => {
        return isActive ? (
            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                Attivo
            </Badge>
        ) : (
            <Badge variant="destructive">Inattivo</Badge>
        );
    };

    // Filter users to show ONLY those with Discord ID
    const discordUsers = users.filter((u) => u.discord_id);
    const filteredUsers = discordUsers;

    return (
        <AdminLayout user={user} onLogout={handleLogout}>
            <div className="space-y-6 sm:space-y-8">
                <EnterprisePageHeader
                    badge="Users"
                    title="User Management"
                    subtitle="Gestione utenti con Discord ID, ruoli granulari e sicurezza operativa"
                    icon={<Users className="h-6 w-6" />}
                    isLoading={isLoading}
                    onRefresh={refresh}
                />

                {/* Stats Overview */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-blue-500" />
                                    Utenti Discord
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Live
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {discordUsers.length}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {discordUsers.filter((u) => u.is_active).length}{" "}
                                attivi
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-4 w-4 text-green-500" />
                                    Admin
                                </div>
                                <Badge variant="default" className="text-xs">
                                    Sicuro
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {
                                    discordUsers.filter(
                                        (u) =>
                                            u.role === "ADMIN" ||
                                            u.role === "SUPER_ADMIN",
                                    ).length
                                }
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                Staff autorizzato
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Smartphone className="h-4 w-4 text-orange-500" />
                                    MFA Attivo
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    2FA
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {
                                    discordUsers.filter((u) => u.mfa_enabled)
                                        .length
                                }
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                Sicurezza extra
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-purple-500" />
                                    Trial Users
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Temp
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {
                                    discordUsers.filter(
                                        (u) => u.role === "TRIAL",
                                    ).length
                                }
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                Accesso temporaneo
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Search and Filters */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Search className="h-5 w-5 text-primary" />
                            Ricerca e Filtri
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col gap-4">
                            <div className="flex-1">
                                <Input
                                    placeholder="Cerca per username, email o Discord..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                    onKeyPress={(e) =>
                                        e.key === "Enter" && handleSearch()
                                    }
                                />
                            </div>
                            <select
                                className="px-3 py-2 border border-input bg-background rounded-md"
                                value={selectedRole}
                                onChange={(e) =>
                                    setSelectedRole(
                                        e.target.value as UserRole | "",
                                    )
                                }
                            >
                                <option value="">Tutti i ruoli</option>
                                <option value="SUPER_ADMIN">Super Admin</option>
                                <option value="ADMIN">Admin</option>
                                <option value="MARRANI">Marrani</option>
                                    <option value="TRIAL">Trial</option>
                            </select>
                        </div>
                        <EnterpriseStickyActionBar>
                            <Button
                                variant="outline"
                                onClick={() =>
                                    setShowActiveOnly(!showActiveOnly)
                                }
                                className={
                                    showActiveOnly ? "bg-primary/10" : ""
                                }
                            >
                                <UserCheck className="h-4 w-4 mr-2" />
                                Solo Attivi
                            </Button>
                            <Button onClick={handleSearch} className="ml-auto">
                                <Search className="h-4 w-4 mr-2" />
                                Cerca
                            </Button>
                        </EnterpriseStickyActionBar>
                    </CardContent>
                </Card>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
                    {/* Users List */}
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users className="h-5 w-5 text-primary" />
                                    Utenti Discord ({filteredUsers.length})
                                </div>
                                <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                                    Solo Discord ID
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-72 sm:h-96">
                                {isLoading ? (
                                    <EnterpriseSectionSkeleton rows={6} showHeader={false} rowClassName="h-24" />
                                ) : filteredUsers.length > 0 ? (
                                    <div className="space-y-3">
                                        {filteredUsers.map((userItem) => (
                                            <div
                                                key={userItem.id}
                                                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
                                                    selectedUser?.id === userItem.id
                                                        ? "border-primary bg-primary/5"
                                                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                                                }`}
                                                onClick={() => selectUser(userItem)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-semibold">
                                                            {userItem.discord_avatar ? (
                                                                <img
                                                                    src={
                                                                        userItem.discord_avatar
                                                                    }
                                                                    alt={
                                                                        userItem.username
                                                                    }
                                                                    className="w-10 h-10 rounded-full"
                                                                />
                                                            ) : (
                                                                userItem.username
                                                                    .charAt(0)
                                                                    .toUpperCase()
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold flex items-center gap-2">
                                                                {userItem.discord_username ||
                                                                    userItem.username}
                                                                {userItem.mfa_enabled && (
                                                                    <Smartphone className="h-3 w-3 text-green-500" />
                                                                )}
                                                            </h3>
                                                            <p className="text-sm text-muted-foreground">
                                                                ID:{" "}
                                                                {
                                                                    userItem.discord_id
                                                                }
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right space-y-1">
                                                        {getRoleBadge(
                                                            userItem.role,
                                                        )}
                                                        {getStatusBadge(
                                                            userItem.is_active,
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <EnterpriseEmptyState
                                        icon={<Users className="h-10 w-10 opacity-50" />}
                                        title="Nessun utente trovato"
                                        description="Nessun account con Discord ID corrisponde ai filtri attivi."
                                    />
                                )}
                            </ScrollArea>
                        </CardContent>
                    </Card>

                    {/* User Details */}
                    {selectedUser ? (
                        <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <UserCog className="h-5 w-5 text-primary" />
                                        {selectedUser.discord_username ||
                                            selectedUser.username}
                                    </div>
                                    {getRoleBadge(selectedUser.role)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Tabs defaultValue="details" className="w-full">
                                    <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3">
                                        <TabsTrigger value="details" className="text-xs sm:text-sm">
                                            Dettagli
                                        </TabsTrigger>
                                        <TabsTrigger value="sessions" className="text-xs sm:text-sm">
                                            Sessioni
                                        </TabsTrigger>
                                        <TabsTrigger value="actions" className="text-xs sm:text-sm">
                                            Azioni
                                        </TabsTrigger>
                                    </TabsList>

                                    <TabsContent
                                        value="details"
                                        className="space-y-4"
                                    >
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                            <div>
                                                <p className="text-sm text-muted-foreground">
                                                    Discord ID
                                                </p>
                                                <p className="font-mono text-sm">
                                                    {selectedUser.discord_id}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">
                                                    Username
                                                </p>
                                                <p className="font-medium">
                                                    {selectedUser.discord_username ||
                                                        selectedUser.username}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">
                                                    Email
                                                </p>
                                                <p className="text-sm">
                                                    {selectedUser.email ||
                                                        "Non disponibile"}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">
                                                    Registrato
                                                </p>
                                                <p className="text-sm">
                                                    {new Date(
                                                        selectedUser.created_at,
                                                    ).toLocaleDateString(
                                                        "it-IT",
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold flex items-center gap-2">
                                                <Settings className="h-4 w-4" />
                                                Gestione Ruolo
                                            </h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {(
                                                    [
                                                        "SUPER_ADMIN",
                                                        "ADMIN",
                                                        "MARRANI",
                                                        "TRIAL",
                                                    ] as UserRole[]
                                                ).map((role) => (
                                                    <Button
                                                        key={role}
                                                        variant={
                                                            selectedUser.role ===
                                                            role
                                                                ? "default"
                                                                : "outline"
                                                        }
                                                        size="sm"
                                                        onClick={() =>
                                                            handleRoleChange(
                                                                selectedUser.id,
                                                                role,
                                                            )
                                                        }
                                                        disabled={
                                                            actionLoading ===
                                                            `role-${selectedUser.id}`
                                                        }
                                                    >
                                                        {role ===
                                                            "SUPER_ADMIN" && (
                                                            <Crown className="h-3 w-3 mr-1" />
                                                        )}
                                                        {role === "ADMIN" && (
                                                            <ShieldCheck className="h-3 w-3 mr-1" />
                                                        )}
                                                        {role === "MARRANI" && (
                                                            <UserCheck className="h-3 w-3 mr-1" />
                                                        )}
                                                        {role === "TRIAL" && (
                                                            <Clock className="h-3 w-3 mr-1" />
                                                        )}
                                                        {role.replace("_", " ")}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold flex items-center gap-2">
                                                <Activity className="h-4 w-4" />
                                                Status Account
                                            </h4>
                                            <Button
                                                variant={
                                                    selectedUser.is_active
                                                        ? "destructive"
                                                        : "default"
                                                }
                                                onClick={() =>
                                                    handleToggleStatus(
                                                        selectedUser.id,
                                                    )
                                                }
                                                disabled={
                                                    actionLoading ===
                                                    `status-${selectedUser.id}`
                                                }
                                                className="w-full"
                                            >
                                                {selectedUser.is_active ? (
                                                    <>
                                                        <Ban className="h-4 w-4 mr-2" />
                                                        Disattiva Utente
                                                    </>
                                                ) : (
                                                    <>
                                                        <Unlock className="h-4 w-4 mr-2" />
                                                        Attiva Utente
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </TabsContent>

                                    <TabsContent
                                        value="sessions"
                                        className="space-y-4"
                                    >
                                        <h4 className="font-semibold flex items-center gap-2">
                                            <Globe className="h-4 w-4" />
                                            Sessioni Attive (
                                            {userSessions.length})
                                        </h4>
                                        <ScrollArea className="h-48">
                                            {userSessions.length > 0 ? (
                                                <div className="space-y-2">
                                                    {userSessions.map(
                                                        (session) => (
                                                            <div
                                                                key={session.id}
                                                                className="p-3 bg-muted/50 rounded border"
                                                            >
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="font-medium text-sm">
                                                                        {
                                                                            session.ip_address
                                                                        }
                                                                    </span>
                                                                    {session.is_current && (
                                                                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-xs">
                                                                            Corrente
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-muted-foreground truncate">
                                                                    {
                                                                        session.user_agent
                                                                    }
                                                                </p>
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    Ultima
                                                                    attività:{" "}
                                                                    {new Date(
                                                                        session.last_activity,
                                                                    ).toLocaleString(
                                                                        "it-IT",
                                                                    )}
                                                                </p>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            ) : (
                                                <EnterpriseEmptyState
                                                    icon={<Globe className="h-8 w-8 opacity-50" />}
                                                    title="Nessuna sessione attiva"
                                                    description="L'utente non ha sessioni correnti registrate."
                                                />
                                            )}
                                        </ScrollArea>
                                    </TabsContent>

                                    <TabsContent
                                        value="actions"
                                        className="space-y-4"
                                    >
                                        <h4 className="font-semibold flex items-center gap-2">
                                            <Clock className="h-4 w-4" />
                                            Log Attività Recenti
                                        </h4>
                                        <ScrollArea className="h-48">
                                            {auditLogs.length > 0 ? (
                                                <div className="space-y-2">
                                                    {auditLogs.map((log) => (
                                                        <div
                                                            key={log.id}
                                                            className="p-3 bg-muted/50 rounded border"
                                                        >
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <CheckCircle className="h-3 w-3 text-green-500" />
                                                                <span className="font-medium text-sm">
                                                                    {log.action}
                                                                </span>
                                                                <span className="text-xs text-muted-foreground ml-auto">
                                                                    {new Date(
                                                                        log.timestamp,
                                                                    ).toLocaleString(
                                                                        "it-IT",
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">
                                                                {log.details}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                IP:{" "}
                                                                {log.ip_address}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <EnterpriseEmptyState
                                                    icon={<Clock className="h-8 w-8 opacity-50" />}
                                                    title="Nessuna attivita recente"
                                                    description="Nessun evento audit disponibile per questo utente."
                                                />
                                            )}
                                        </ScrollArea>
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                            <CardContent className="flex items-center justify-center h-72 sm:h-96">
                                <EnterpriseEmptyState
                                    icon={<Users className="h-12 w-12 opacity-50" />}
                                    title="Seleziona un utente"
                                    description="Clicca su un utente per visualizzare dettagli, sessioni e azioni staff."
                                    action={(
                                        <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                                            Solo utenti con Discord ID
                                        </Badge>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Error Display */}
                {error && (
                    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <span className="font-medium">
                                    Errore di connessione
                                </span>
                            </div>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                {error}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
}
